import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PaginationState } from '@tanstack/react-table'
import { useDynamicSegment } from 'src/lib/contexts/DynamicSegmentContext'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { postRequest } from 'src/lib/core/api-client'
import { apiURI } from 'src/lib/utils/constants'
import { errorToast } from 'src/lib/utils/toasters'
import { convertFiltersToAudienceDefinition } from 'src/lib/utils/segmentsHelper'

interface PreviewTableRow {
  [key: string]: string | null
}

interface PreviewTableData {
  fields: Record<string, { type: string }>
  rows: Array<Record<string, { value: { raw: string | null; formatted: string } }>>
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

/** Nested `data.data` from segments engine POST responses (getResponseParams shape). */
function readSortmentInnerPayload(value: unknown): Record<string, unknown> | undefined {
  if (!isRecord(value)) return undefined
  const d1 = value['data']
  if (!isRecord(d1)) return undefined
  const inner = d1['data']
  if (!isRecord(inner)) return undefined
  return inner
}

interface SortmentAudienceCountPayload {
  count?: number | null
  status?: string
}

interface SortmentPreviewCell {
  value?: { raw?: string | null; formatted?: string | null }
}

interface SortmentAudiencePreviewPayload {
  status?: string
  rows?: Array<Record<string, SortmentPreviewCell>>
  fields?: Record<string, { type: string }>
}

function asCountPayload(inner: Record<string, unknown> | undefined): SortmentAudienceCountPayload | undefined {
  if (!inner) return undefined
  return inner as SortmentAudienceCountPayload
}

function asPreviewPayload(inner: Record<string, unknown> | undefined): SortmentAudiencePreviewPayload | undefined {
  if (!inner) return undefined
  return inner as SortmentAudiencePreviewPayload
}

const SORTMENT_POLL_INTERVAL_MS = 3000

/** Visual job-stage fill (0–100). Sortment count/preview APIs do not return a percent. */
const JOB_STAGE_CLICK_PERCENT = 12
const JOB_STAGE_PENDING_BASE_PERCENT = 28
const JOB_STAGE_PROCESSING_BASE_PERCENT = 52
const JOB_STAGE_POLL_STEP_PERCENT = 10
const JOB_STAGE_POLL_CAP_PERCENT = 90
const JOB_STAGE_READY_PERCENT = 100
/** Matches Button dashed determinate `after:ui-duration-500`. Same-tick `loading=false` batches away the 100% paint. */
const JOB_STAGE_READY_HOLD_MS = 500

function scheduleReadyHold(
  timeoutRef: { current: ReturnType<typeof setTimeout> | null },
  isCurrent: () => boolean,
  onSettled: () => void
) {
  if (timeoutRef.current) {
    clearTimeout(timeoutRef.current)
  }
  // Hold 100% for the fill width transition. The bar is a ::after, so transitionend is not a reliable hook without changing Button.
  timeoutRef.current = setTimeout(() => {
    timeoutRef.current = null
    if (!isCurrent()) {
      return
    }
    onSettled()
  }, JOB_STAGE_READY_HOLD_MS)
}

function pollStagePercent(status: string | undefined, pollIndex: number): number {
  const base =
    status === 'PROCESSING' ? JOB_STAGE_PROCESSING_BASE_PERCENT : JOB_STAGE_PENDING_BASE_PERCENT
  return Math.min(JOB_STAGE_POLL_CAP_PERCENT, base + pollIndex * JOB_STAGE_POLL_STEP_PERCENT)
}

function scheduleSortmentPoll(run: () => void): ReturnType<typeof setTimeout> {
  return setTimeout(run, SORTMENT_POLL_INTERVAL_MS)
}

const MOCK_PREVIEW_ROW_COUNT = 25
const MOCK_CHARSET = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'

function hashSeed(seed: string): number {
  let h = 0
  for (let i = 0; i < seed.length; i += 1) {
    h = (Math.imul(31, h) + seed.charCodeAt(i)) | 0
  }
  return Math.abs(h)
}

function buildMockPreviewRows(dimensionIds: string[], rowCount: number): PreviewTableRow[] {
  return Array.from({ length: rowCount }, (_, rowIndex) => {
    const row: PreviewTableRow = {}
    dimensionIds.forEach((dimensionId, colIndex) => {
      let h = hashSeed(`${dimensionId}:${rowIndex}:${colIndex}`)
      const len = 5 + (h % 10)
      let chunk = ''
      for (let i = 0; i < len; i += 1) {
        h = (Math.imul(1103515245, h + i) + 12345) | 0
        chunk += MOCK_CHARSET[Math.abs(h) % MOCK_CHARSET.length]
      }
      row[dimensionId] = chunk
    })
    return row
  })
}

export function useDynamicSegmentPreview(options: {
  nudgeOnly: boolean
  onEngageFullPreview?: () => void
}) {
  const { nudgeOnly, onEngageFullPreview } = options
  const { segmentFormState, relationData, hasPreviewChanges, isEditMode, isLoadingSegment, actions } =
    useDynamicSegment()
  const { validateForm, resetPreviewChanges } = actions
  const {
    getters: { merchant }
  } = useMerchant()

  const [audienceCount, setAudienceCount] = useState<number | null>(null)
  const [loadingCount, setLoadingCount] = useState(false)
  const [countLoadProgress, setCountLoadProgress] = useState(0)
  const [tickerIndex, setTickerIndex] = useState(0)
  const [loadingTable, setLoadingTable] = useState(false)
  const [tableLoadProgress, setTableLoadProgress] = useState(0)
  const [previewData, setPreviewData] = useState<PreviewTableRow[]>([])
  const [paginationState, setPaginationState] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25
  })

  const countPollRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const previewPollRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const countRequestGenerationRef = useRef(0)
  const previewRequestGenerationRef = useRef(0)
  const fetchPreviewDataRef = useRef<() => void>(() => {})
  const nudgeOnlyRef = useRef(nudgeOnly)
  const hasPreviewChangesRef = useRef(hasPreviewChanges)

  const merchantId = merchant?.accountId || ''

  const dimensionIds = useMemo(
    () => segmentFormState.previewFilters.map(filter => filter.id),
    [segmentFormState.previewFilters]
  )
  const dimensionNames = useMemo(() => {
    const names: Record<string, string> = {}
    for (const filter of segmentFormState.previewFilters) {
      names[filter.id] = filter.name
    }
    return names
  }, [segmentFormState.previewFilters])

  const hasConditions =
    segmentFormState.filters.length > 0 || (isEditMode && isLoadingSegment)

  const audienceDefinitionSignature = useMemo(
    () => JSON.stringify(convertFiltersToAudienceDefinition(segmentFormState.filters)),
    [segmentFormState.filters]
  )

  const showEngagementOverlay = nudgeOnly || hasPreviewChanges

  const mockPreviewRowsFull = useMemo(
    () =>
      dimensionIds.length > 0 ? buildMockPreviewRows(dimensionIds, MOCK_PREVIEW_ROW_COUNT) : [],
    [dimensionIds]
  )

  /** Keep mock rows visible under the engagement blur while preview API loads (avoid skeleton swapping). */
  const usePlaceholderPreviewTable =
    showEngagementOverlay && previewData.length === 0 && dimensionIds.length > 0

  const previewTableLoading = loadingTable && !usePlaceholderPreviewTable

  const tablePreviewData = useMemo(() => {
    if (!usePlaceholderPreviewTable) {
      return previewData
    }
    const { pageIndex, pageSize } = paginationState
    const start = pageIndex * pageSize
    return mockPreviewRowsFull.slice(start, start + pageSize)
  }, [
    usePlaceholderPreviewTable,
    previewData,
    mockPreviewRowsFull,
    paginationState.pageIndex,
    paginationState.pageSize
  ])

  const knownTotalRowCount = usePlaceholderPreviewTable ? MOCK_PREVIEW_ROW_COUNT : undefined

  const fetchAudienceCount = useCallback(async () => {
    if (!relationData.relationId) {
      setAudienceCount(null)
      return
    }

    const errorCount = validateForm({ skipName: true })
    if (errorCount > 0) {
      errorToast('Please check the errors in the form and try again.')
      return
    }

    if (countPollRef.current) clearTimeout(countPollRef.current)

    const requestGeneration = ++countRequestGenerationRef.current

    setLoadingCount(true)
    setCountLoadProgress(JOB_STAGE_CLICK_PERCENT)
    const payload = {
      audience_definition: convertFiltersToAudienceDefinition(segmentFormState.filters)
    }

    let pollIndex = 0
    const pollCount = async () => {
      if (requestGeneration !== countRequestGenerationRef.current) {
        return
      }
      try {
        const response = await postRequest(
          `/merchant/${merchantId}/segments/sortment/relations/${relationData.relationId}/audiences/count`,
          apiURI.SEGMENTS_ENGINE,
          payload
        )
        const data = asCountPayload(readSortmentInnerPayload(response as unknown))
        const hasCount = data?.count != null && typeof data.count === 'number'
        const isPending = data?.status === 'PENDING' || data?.status === 'PROCESSING'
        const isReady = data?.status === 'READY' || (hasCount && !isPending)
        if (requestGeneration !== countRequestGenerationRef.current) {
          return
        }
        if (isReady) {
          setCountLoadProgress(JOB_STAGE_READY_PERCENT)
          setAudienceCount(data?.count ?? null)
          scheduleReadyHold(
            countPollRef,
            () => requestGeneration === countRequestGenerationRef.current,
            () => {
              setLoadingCount(false)
            }
          )
        } else if (data?.status === 'FAILED') {
          setAudienceCount(null)
          setCountLoadProgress(0)
          setLoadingCount(false)
        } else {
          setCountLoadProgress(pollStagePercent(data?.status, pollIndex))
          pollIndex += 1
          countPollRef.current = scheduleSortmentPoll(() => {
            void pollCount()
          })
        }
      } catch (error) {
        console.error('Error fetching audience count:', error)
        if (requestGeneration !== countRequestGenerationRef.current) {
          return
        }
        setAudienceCount(null)
        setCountLoadProgress(0)
        setLoadingCount(false)
      }
    }

    void pollCount()
  }, [relationData.relationId, merchantId, validateForm, segmentFormState.filters])

  const fetchPreviewData = useCallback(async () => {
    const dimensions = segmentFormState.previewFilters.map(f => f.id)
    if (!relationData.relationId || dimensions.length === 0) {
      setPreviewData([])
      return
    }
    const errorCount = validateForm({ skipName: true })
    if (errorCount > 0) {
      errorToast('Please check the errors in the form and try again.')
      return
    }

    if (previewPollRef.current) clearTimeout(previewPollRef.current)

    const requestGeneration = ++previewRequestGenerationRef.current

    setLoadingTable(true)
    setTableLoadProgress(JOB_STAGE_CLICK_PERCENT)
    const payload = {
      audience_definition: convertFiltersToAudienceDefinition(segmentFormState.filters),
      dimensions,
      limit: paginationState.pageSize,
      offset: paginationState.pageIndex * paginationState.pageSize
    }

    let pollIndex = 0
    const pollPreview = async () => {
      if (requestGeneration !== previewRequestGenerationRef.current) {
        return
      }
      try {
        const response = await postRequest(
          `/merchant/${merchantId}/segments/sortment/relations/${relationData.relationId}/audiences/preview`,
          apiURI.SEGMENTS_ENGINE,
          payload
        )
        if (requestGeneration !== previewRequestGenerationRef.current) {
          return
        }
        const data = asPreviewPayload(readSortmentInnerPayload(response as unknown))
        const hasRows = Array.isArray(data?.rows)
        const isPending = data?.status === 'PENDING' || data?.status === 'PROCESSING'
        const isReady = data?.status === 'READY' || (hasRows && !isPending)
        if (isReady) {
          const apiData: PreviewTableData = data as PreviewTableData
          const transformedRows: PreviewTableRow[] = (apiData.rows ?? []).map(row => {
            const transformedRow: PreviewTableRow = {}
            Object.keys(row).forEach(key => {
              transformedRow[key] = row[key]?.value?.formatted || row[key]?.value?.raw || null
            })
            return transformedRow
          })
          if (requestGeneration !== previewRequestGenerationRef.current) {
            return
          }
          setTableLoadProgress(JOB_STAGE_READY_PERCENT)
          scheduleReadyHold(
            previewPollRef,
            () => requestGeneration === previewRequestGenerationRef.current,
            () => {
              setPreviewData(transformedRows)
              resetPreviewChanges()
              setLoadingTable(false)
            }
          )
        } else if (data?.status === 'FAILED') {
          if (requestGeneration !== previewRequestGenerationRef.current) {
            return
          }
          setPreviewData([])
          setTableLoadProgress(0)
          setLoadingTable(false)
        } else {
          setTableLoadProgress(pollStagePercent(data?.status, pollIndex))
          pollIndex += 1
          previewPollRef.current = scheduleSortmentPoll(() => {
            void pollPreview()
          })
        }
      } catch (error) {
        console.error('Error fetching preview data:', error)
        if (requestGeneration !== previewRequestGenerationRef.current) {
          return
        }
        setPreviewData([])
        setTableLoadProgress(0)
        setLoadingTable(false)
      }
    }

    void pollPreview()
  }, [
    relationData.relationId,
    segmentFormState.previewFilters,
    paginationState,
    merchantId,
    validateForm,
    segmentFormState.filters,
    resetPreviewChanges
  ])

  const handlePreviewDataClick = useCallback(() => {
    if (nudgeOnly) {
      onEngageFullPreview?.()
    }
    void fetchPreviewData()
  }, [nudgeOnly, onEngageFullPreview, fetchPreviewData])

  const resetAudienceCountForNewDefinition = useCallback(() => {
    countRequestGenerationRef.current += 1
    setAudienceCount(null)
    setLoadingCount(false)
    setCountLoadProgress(0)
    if (countPollRef.current) {
      clearTimeout(countPollRef.current)
      countPollRef.current = null
    }
  }, [])

  // External sync: audience definition identity — reset count + clear count poll when filters change
  useEffect(() => {
    resetAudienceCountForNewDefinition()
  }, [audienceDefinitionSignature, resetAudienceCountForNewDefinition])

  fetchPreviewDataRef.current = fetchPreviewData
  nudgeOnlyRef.current = nudgeOnly
  hasPreviewChangesRef.current = hasPreviewChanges

  // External sync: preview pagination — refetch table when page/size changes (nudge/dirty gated via refs)
  useEffect(() => {
    if (nudgeOnlyRef.current) return
    if (hasPreviewChangesRef.current) return
    void fetchPreviewDataRef.current()
  }, [paginationState.pageIndex, paginationState.pageSize])

  // External sync: unmount — abort in-flight poll timers + invalidate request generations
  useEffect(() => {
    return () => {
      if (countPollRef.current) clearTimeout(countPollRef.current)
      if (previewPollRef.current) clearTimeout(previewPollRef.current)
      previewRequestGenerationRef.current += 1
      countRequestGenerationRef.current += 1
    }
  }, [])

  // External sync: timer for loading-count ticker dots while audience count request is in flight
  useEffect(() => {
    if (!loadingCount) {
      setTickerIndex(0)
      return
    }
    const id = setInterval(() => {
      setTickerIndex((i) => (i + 1) % 3)
    }, 2500)
    return () => clearInterval(id)
  }, [loadingCount])

  return {
    audienceCount,
    tickerIndex,
    paginationState,
    setPaginationState,
    dimensionIds,
    dimensionNames,
    hasConditions,
    showEngagementOverlay,
    tablePreviewData,
    knownTotalRowCount,
    previewTableLoading,
    countJob: { isLoading: loadingCount, stagePercent: countLoadProgress },
    previewJob: { isLoading: loadingTable, stagePercent: tableLoadProgress },
    fetchAudienceCount,
    handlePreviewDataClick
  }
}
